"use client";

import Image from "next/image";
import Link from "next/link";
import { useState } from "react";
import {
  CalendarIcon,
  ClockIcon,
  MapPinIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
} from "@heroicons/react/24/outline";
import type { Event } from "@/types";

type EventsMobileViewProps = {
  events: Event[];
  pagination: {
    currentPage: number;
    lastPage: number;
    total: number;
  };
};

function EventCard({ event }: { event: Event }) {
  const [imageLoading, setImageLoading] = useState(true);

  return (
    <Link
      href={event.slug ? `/evento/${event.slug}` : "#"}
      className="flex bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all"
    >
      {/* Image */}
      <div className="relative w-28 h-28 shrink-0 bg-gray-100">
        {imageLoading && (
          <div className="absolute inset-0 bg-gray-200 animate-pulse" />
        )}
        <Image
          src={event.image}
          alt={event.title}
          fill
          className={`object-cover transition-opacity ${
            imageLoading ? "opacity-0" : "opacity-100"
          }`}
          onLoad={() => setImageLoading(false)}
          unoptimized
        />
      </div>

      {/* Content */}
      <div className="flex-1 p-3 flex flex-col justify-between min-w-0">
        <div>
          <h3 className="font-semibold text-gray-900 line-clamp-2 text-sm leading-tight mb-1.5">
            {event.title}
          </h3>

          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
            <div className="flex items-center gap-1">
              <CalendarIcon className="w-3.5 h-3.5 text-mygreen" />
              <span>{event.date}</span>
            </div>
            <div className="flex items-center gap-1">
              <ClockIcon className="w-3.5 h-3.5 text-mygreen" />
              <span>{event.hour}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
          <MapPinIcon className="w-3.5 h-3.5 text-mygreen shrink-0" />
          <span className="truncate">{event.location}</span>
        </div>
      </div>

      {/* Arrow */}
      <div className="flex items-center pr-3">
        <ChevronRightIcon className="w-5 h-5 text-gray-300" />
      </div>
    </Link>
  );
}

export default function EventsMobileView({
  events,
  pagination,
}: EventsMobileViewProps) {
  if (events.length === 0) {
    return (
      <div className="md:hidden py-12 px-4">
        <div className="max-w-lg mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <p className="text-sm uppercase tracking-widest text-mygreen font-semibold mb-2">
              Agenda Santa Fé
            </p>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Eventos da Cidade
            </h1>
            <p className="text-gray-600">
              Confira tudo o que vai acontecer em Santa Fé do Sul
            </p>
          </div>

          {/* Empty State */}
          <div className="bg-white rounded-2xl p-8 shadow-sm text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
              <CalendarIcon className="w-8 h-8 text-gray-400" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              Nenhum evento encontrado
            </h2>
            <p className="text-gray-500 mb-6">
              Ainda não temos eventos cadastrados. Volte em breve!
            </p>
            <div className="flex flex-col gap-3">
              <Link
                href="/"
                className="inline-flex items-center justify-center px-6 py-3 bg-mygreen text-white rounded-full font-semibold hover:bg-mygreen/90 transition-colors"
              >
                Voltar para Home
              </Link>
              <Link
                href="/contato"
                className="inline-flex items-center justify-center px-6 py-3 border-2 border-mygreen text-mygreen rounded-full font-semibold hover:bg-mygreen/10 transition-colors"
              >
                Divulgar meu evento
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="md:hidden py-8 pb-24">
      {/* Header */}
      <div className="px-4 mb-6">
        <p className="text-sm uppercase tracking-widest text-mygreen font-semibold mb-1">
          Agenda Santa Fé
        </p>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Eventos da Cidade
        </h1>
        <p className="text-gray-600 text-sm">
          Do show na praça aos encontros de empreendedorismo, descubra tudo o
          que vai acontecer.
        </p>
      </div>

      {/* Stats Bar */}
      <div className="px-4 mb-6">
        <div className="bg-mygreen/10 rounded-xl px-4 py-3 flex items-center justify-between">
          <span className="text-mygreen font-medium">
            {pagination.total} {pagination.total === 1 ? "evento" : "eventos"}{" "}
            encontrados
          </span>
          {pagination.lastPage > 1 && (
            <span className="text-mygreen/70 text-sm">
              Página {pagination.currentPage} de {pagination.lastPage}
            </span>
          )}
        </div>
      </div>

      {/* Events Grid */}
      <div className="px-4 space-y-3">
        {events.map((event) => (
          <EventCard key={event.id} event={event} />
        ))}
      </div>

      {/* Pagination */}
      {pagination.lastPage > 1 && (
        <div className="px-4 mt-8">
          <Pagination
            currentPage={pagination.currentPage}
            lastPage={pagination.lastPage}
          />
        </div>
      )}
    </div>
  );
}

function Pagination({
  currentPage,
  lastPage,
}: {
  currentPage: number;
  lastPage: number;
}) {
  const prevDisabled = currentPage <= 1;
  const nextDisabled = currentPage >= lastPage;

  // Generate page numbers
  const getPageNumbers = () => {
    const pages: (number | "...")[] = [];

    if (lastPage <= 5) {
      for (let i = 1; i <= lastPage; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      if (currentPage > 3) {
        pages.push("...");
      }

      for (
        let i = Math.max(2, currentPage - 1);
        i <= Math.min(lastPage - 1, currentPage + 1);
        i++
      ) {
        if (!pages.includes(i)) {
          pages.push(i);
        }
      }

      if (currentPage < lastPage - 2) {
        pages.push("...");
      }

      if (!pages.includes(lastPage)) {
        pages.push(lastPage);
      }
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();

  return (
    <nav
      className="flex items-center justify-center gap-1.5"
      aria-label="Paginação"
    >
      {/* First Page */}
      {currentPage > 2 && (
        <Link
          href="/eventos"
          className="flex items-center justify-center w-9 h-9 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
          aria-label="Primeira página"
        >
          <ChevronDoubleLeftIcon className="w-4 h-4" />
        </Link>
      )}

      {/* Previous */}
      {prevDisabled ? (
        <span className="flex items-center justify-center w-9 h-9 rounded-lg text-gray-300 cursor-not-allowed">
          <ChevronLeftIcon className="w-4 h-4" />
        </span>
      ) : (
        <Link
          href={
            currentPage === 2 ? "/eventos" : `/eventos?page=${currentPage - 1}`
          }
          className="flex items-center justify-center w-9 h-9 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
          aria-label="Página anterior"
        >
          <ChevronLeftIcon className="w-4 h-4" />
        </Link>
      )}

      {/* Page Numbers */}
      <div className="flex items-center gap-1 mx-1">
        {pageNumbers.map((page, index) =>
          page === "..." ? (
            <span
              key={`ellipsis-${index}`}
              className="flex items-center justify-center w-9 h-9 text-gray-400 text-sm"
            >
              ···
            </span>
          ) : (
            <Link
              key={page}
              href={page === 1 ? "/eventos" : `/eventos?page=${page}`}
              className={`flex items-center justify-center w-9 h-9 rounded-lg font-medium text-sm transition-all ${
                page === currentPage
                  ? "bg-mygreen text-white"
                  : "text-gray-600 hover:bg-gray-100"
              }`}
              aria-current={page === currentPage ? "page" : undefined}
            >
              {page}
            </Link>
          )
        )}
      </div>

      {/* Next */}
      {nextDisabled ? (
        <span className="flex items-center justify-center w-9 h-9 rounded-lg text-gray-300 cursor-not-allowed">
          <ChevronRightIcon className="w-4 h-4" />
        </span>
      ) : (
        <Link
          href={`/eventos?page=${currentPage + 1}`}
          className="flex items-center justify-center w-9 h-9 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
          aria-label="Próxima página"
        >
          <ChevronRightIcon className="w-4 h-4" />
        </Link>
      )}

      {/* Last Page */}
      {currentPage < lastPage - 1 && (
        <Link
          href={`/eventos?page=${lastPage}`}
          className="flex items-center justify-center w-9 h-9 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
          aria-label="Última página"
        >
          <ChevronDoubleRightIcon className="w-4 h-4" />
        </Link>
      )}
    </nav>
  );
}
